<template>
    <div>
        <div v-if="classroom" class="mb-3 p-3 bg-light rounded d-flex justify-content-between align-items-center flex-wrap">
            <div>
                <strong>{{ $t('classroom') }}:</strong> {{ classroom.classroom_name || '-' }}
            </div>
            <div>
                <strong>{{ $t('capacity') }}:</strong> {{ classroom.capacity || '-' }}
            </div>
            <div>
                <strong>{{ $t('student_count') }}:</strong> {{ students.length }}
            </div>
        </div>

        <div v-if="checkPermission('placementexamclassroom_storestudent')" class="card mb-4 border shadow-none">
            <div class="card-header bg-white font-weight-bold">
                <i class="ri-user-add-line mr-1 text-primary"></i> {{ $t('add_student') }}
            </div>
            <div class="card-body">
                <ValidationObserver ref="addStudentForm" v-slot="{ handleSubmit }">
                    <form @submit.prevent="handleSubmit(addStudent)">
                        <div class="row align-items-end">
                            <div class="col-12 col-md-5">
                                <ValidationProvider name="student_number" rules="required" v-slot="{ valid, errors }">
                                    <b-form-group :label="$t('student_number')">
                                        <b-form-input
                                            v-model.trim="newStudent.student_number"
                                            :placeholder="$t('student_number')"
                                            maxlength="12"
                                            :state="errors[0] ? false : (valid ? true : null)"
                                        ></b-form-input>
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>
                            <div class="col-12 col-md-4">
                                <ValidationProvider name="seat" rules="required" v-slot="{ valid, errors }">
                                    <b-form-group :label="$t('seat')">
                                        <b-form-input
                                            type="number"
                                            min="1"
                                            step="1"
                                            v-model.number="newStudent.seat"
                                            :placeholder="$t('seat')"
                                            :state="errors[0] ? false : (valid ? true : null)"
                                        ></b-form-input>
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>
                            <div class="col-12 col-md-3 mb-3">
                                <b-button
                                    type="submit"
                                    variant="primary"
                                    block
                                    :disabled="isSubmitting"
                                >
                                    <b-spinner small v-if="isSubmitting"></b-spinner>
                                    <i class="ri-add-line mr-1" v-else></i>
                                    {{ $t('add') }}
                                </b-button>
                            </div>
                        </div>
                    </form>
                </ValidationObserver>
            </div>
        </div>

        <div class="card border shadow-none">
            <div class="card-header bg-white d-flex justify-content-between align-items-center font-weight-bold">
                <div>
                    <i class="ri-group-line mr-1 text-primary"></i> {{ $t('placement_exam_classrooms_students') }}
                </div>
                <b-button variant="outline-secondary" size="sm" @click="fetchStudents" :disabled="isLoading">
                    <i class="ri-refresh-line" :class="{ 'ri-spin': isLoading }"></i>
                </b-button>
            </div>
            <div class="card-body p-0">
                <div v-if="isLoading" class="text-center py-4">
                    <b-spinner variant="primary"></b-spinner>
                    <div class="mt-2 text-muted">{{ $t('loading') }}</div>
                </div>
                <div v-else-if="students.length === 0" class="text-center py-4 text-muted">
                    {{ $t('no_result_2') }}
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-hover table-striped mb-0">
                        <thead class="thead-light">
                            <tr>
                                <th style="width: 120px;">{{ $t('seat') }}</th>
                                <th>{{ $t('student_number') }}</th>
                                <th>{{ $t('name_surname') }}</th>
                                <th v-if="checkPermission('placementexamclassroom_deletestudent')" style="width: 120px;" class="text-center">{{ $t('actions') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(student, index) in students" :key="student.id || index">
                                <td class="align-middle font-weight-bold">
                                    {{ student.seat || '-' }}
                                </td>
                                <td class="align-middle">
                                    {{ student.student_number || '-' }}
                                </td>
                                <td class="align-middle">
                                    {{ getStudentFullName(student) }}
                                </td>
                                <td v-if="checkPermission('placementexamclassroom_deletestudent')" class="align-middle text-center">
                                    <b-button
                                        variant="outline-danger"
                                        size="sm"
                                        :title="$t('delete_student')"
                                        :disabled="deletingStudentNumber === student.student_number"
                                        @click="deleteStudent(student)"
                                    >
                                        <b-spinner small v-if="deletingStudentNumber === student.student_number"></b-spinner>
                                        <span v-else>
                                            <i class="ri-delete-bin-line mr-1"></i>{{ $t('delete') }}
                                        </span>
                                    </b-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import PlacementExamClassroomsService from "@/services/PlacementExamClassroomsService";

export default {
    name: "PlacementExamClassroomStudentList",
    components: {
        ValidationObserver,
        ValidationProvider
    },
    props: {
        classroomId: {
            type: [Number, String],
            default: null
        },
        classroom: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            isLoading: false,
            isSubmitting: false,
            students: [],
            newStudent: {
                student_number: "",
                seat: null
            },
            deletingStudentNumber: null
        };
    },
    methods: {
        fetchStudents() {
            if (!this.classroomId) return;
            this.isLoading = true;

            PlacementExamClassroomsService.show(this.classroomId)
                .then(response => {
                    const data = response.data.data || response.data || {};
                    this.students = data.reservations || data.students || (Array.isArray(data) ? data : []);
                })
                .catch(err => {
                    this.showErrors(err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        async addStudent() {
            if (!this.checkPermission('placementexamclassroom_storestudent')) {
                this.$toast.error(this.$t('you_are_not_authorized_for_this_operation'));
                return;
            }

            const isValid = await this.$refs.addStudentForm.validate();
            if (!isValid) return;

            this.isSubmitting = true;
            const payload = {
                student_number: String(this.newStudent.student_number).trim(),
                seat: parseInt(this.newStudent.seat, 10)
            };

            PlacementExamClassroomsService.storeStudent(this.classroomId, payload)
                .then(response => {
                    this.$toast.success(this.$t('api.' + response.data.message));
                    this.newStudent = {
                        student_number: "",
                        seat: null
                    };
                    if (this.$refs.addStudentForm) {
                        this.$refs.addStudentForm.reset();
                    }
                    this.fetchStudents();
                    this.$emit('refresh');
                })
                .catch(err => {
                    this.showErrors(err);
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },

        getStudentFullName(student) {
            if (student.student && (student.student.full_name || student.student.name)) {
                return student.student.full_name || `${student.student.name || ''} ${student.student.surname || ''}`.trim();
            }
            if (student.full_name) {
                return student.full_name;
            }
            if (student.name || student.surname) {
                return `${student.name || ''} ${student.surname || ''}`.trim();
            }
            return '-';
        },

        deleteStudent(student) {
            if (!this.checkPermission('placementexamclassroom_deletestudent')) {
                this.$toast.error(this.$t('you_are_not_authorized_for_this_operation'));
                return;
            }

            const studentNumber = student.student_number;
            if (!studentNumber) return;

            this.$swal({
                title: this.$t('attention'),
                text: this.$t('are_you_sure_to_delete'),
                showCancelButton: true,
                confirmButtonText: this.$t('yes'),
                cancelButtonText: this.$t('no')
            }).then(result => {
                if (result.isConfirmed) {
                    this.deletingStudentNumber = studentNumber;
                    PlacementExamClassroomsService.deleteStudent(this.classroomId, {
                        student_number: studentNumber
                    })
                        .then(response => {
                            this.$toast.success(this.$t('api.' + (response.data?.message || 'successfully_deleted')));
                            this.fetchStudents();
                            this.$emit('refresh');
                        })
                        .catch(err => {
                            this.showErrors(err);
                        })
                        .finally(() => {
                            this.deletingStudentNumber = null;
                        });
                }
            });
        }
    },
    watch: {
        classroomId: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.fetchStudents();
                } else {
                    this.students = [];
                }
            }
        }
    }
};
</script>
